import { INavLink } from "@fluentui/react";
import { useEffect, useState } from "react";

import { SidebarProps } from "../types/Sidebar.types";

import { SidebarMenuExpandable } from "./SidebarMenuExpandable";
import { SidebarFooter } from "./SidebarMenuFooter";
import { SidebarHeader } from "./SidebarMenuHeader";
import { SidebarMain } from "./SidebarMenuMain";
import { SidebarMenuPanel } from "./SidebarMenuPanel";

import {
  PermissionsAction,
  PermissionsConfig,
  PermissionsPosModule,
  PermissionsScope
} from "@/common/types/permissions.types";
import { useGetProfile } from "@/core/libs/api/react-query/index";
import { useSelectedBu } from "@/hooks/useSelectedBu";

export function Sidebar({ theme }: SidebarProps): JSX.Element {
  const [linksEnabled, setLinksEnabled] = useState<INavLink[] | []>([]);
  const [footerLink, setFooterLink] = useState<INavLink[] | []>([]);

  const userProfileQuery = useGetProfile();
  const { selectedBU, selectedRegion } = useSelectedBu();
  const permissions: PermissionsConfig =
    userProfileQuery.data?.data?.role.permissions;
  const settings = selectedBU?.settings;

  // Modo apenas para região: usuário selecionou uma região/segmento mas nenhuma BU específica
  const isRegionOnlyMode = !selectedBU && Boolean(selectedRegion);

  useEffect(() => {
    const mainLinks = [];
    const footerLink = [];

    if (isRegionOnlyMode) {
      // No modo apenas para região, mostrar apenas Contratos e Faturas,
      // caso o usuário tenha permissão para visualizá-los
      permissions?.[PermissionsScope.POS]?.[
        PermissionsPosModule.CONTRACTS
      ]?.some(
        (action: string) =>
          action === PermissionsAction.ALL || action === PermissionsAction.LIST
      ) &&
        mainLinks.push({
          name: "Contratos",
          url: "/contracts",
          key: "/contracts",
          iconName: "DocumentSet"
        });

      permissions?.[PermissionsScope.POS]?.[
        PermissionsPosModule.INVOICES
      ]?.some(
        (action: PermissionsAction) =>
          action === PermissionsAction.ALL || action === PermissionsAction.LIST
      ) &&
        mainLinks.push({
          name: "Faturas de Cooperados",
          url: "/member-invoices",
          key: "/member-invoices",
          iconName: "PageShared"
        });

      permissions?.[PermissionsScope.ADMIN] &&
        footerLink.push({
          name: "Painel Administrativo",
          url: "/settings",
          key: "/settings",
          iconName: "Settings"
        });

      mainLinks.length > 0 && setLinksEnabled(mainLinks);
      footerLink.length > 0 && setFooterLink(footerLink);

      return;
    }

    permissions?.[PermissionsScope.POS] &&
      mainLinks.push({
        name: "Benefícios",
        url: "/benefits",
        key: "/benefits",
        iconName: "Money"
      });

    permissions?.[PermissionsScope.POS] &&
      settings?.newsLink &&
      mainLinks.push({
        name: "Notícias",
        url: "/news",
        key: "/news",
        iconName: "News"
      });

    permissions?.[PermissionsScope.POS] &&
      settings?.offersLink &&
      mainLinks.push({
        name: "Material de Negócios",
        url: "/business-material",
        key: "/business-material",
        iconName: "AccountActivity"
      });

    permissions?.[PermissionsScope.POS] &&
      settings?.promotionsLink &&
      mainLinks.push({
        name: "Resultado das Campanhas",
        url: "/promotions",
        key: "/promotions",
        iconName: "Savings"
      });

    settings?.powerbi &&
      mainLinks.push({
        name: "Painel de Resultados",
        url: "/results-panel",
        key: "/results-panel",
        iconName: "PowerBILogo"
      });

    permissions?.[PermissionsScope.POS]?.[PermissionsPosModule.ACCOUNTS]?.some(
      (action: string) =>
        action === PermissionsAction.ALL || action === PermissionsAction.LIST
    ) &&
      mainLinks.push({
        name: "Registro dos Cooperados",
        url: "/accounts",
        key: "/accounts",
        iconName: "People"
      });

    permissions?.[PermissionsScope.POS]?.[PermissionsPosModule.CONTRACTS]?.some(
      (action: string) =>
        action === PermissionsAction.ALL || action === PermissionsAction.LIST
    ) &&
      mainLinks.push({
        name: "Contratos",
        url: "/contracts",
        key: "/contracts",
        iconName: "DocumentSet"
      });

    permissions?.[PermissionsScope.POS]?.[PermissionsPosModule.TERMS]?.some(
      (action: string) =>
        action === PermissionsAction.ALL || action === PermissionsAction.LIST
    ) &&
      mainLinks.push(
        {
          name: "Fichas de Matrícula",
          url: "/membership-applications",
          key: "/membership-applications",
          iconName: "ContactCard"
        },
        {
          name: "Documentos Complementares",
          url: "/complementary-documents",
          key: "/complementary-documents",
          iconName: "FabricDocLibrary"
        }
      );

    permissions?.[PermissionsScope.POS]?.[PermissionsPosModule.INVOICES]?.some(
      (action: PermissionsAction) =>
        action === PermissionsAction.ALL || action === PermissionsAction.LIST
    ) &&
      mainLinks.push({
        name: "Faturas de Cooperados",
        url: "/member-invoices",
        key: "/member-invoices",
        iconName: "PageShared"
      });

    permissions?.[PermissionsScope.ADMIN] &&
      footerLink.push({
        name: "Painel Administrativo",
        url: "/settings",
        key: "/settings",
        iconName: "Settings"
      });

    mainLinks.length > 0 && setLinksEnabled(mainLinks);
    footerLink.length > 0 && setFooterLink(footerLink);
  }, [permissions, settings, isRegionOnlyMode]);

  return (
    <>
      <SidebarMenuPanel
        HeaderContent={SidebarHeader}
        MainContent={SidebarMain}
        mainLinks={linksEnabled}
        FooterContent={SidebarFooter}
        footerLinks={footerLink}
        theme={theme}
      />

      <SidebarMenuExpandable
        theme={theme}
        FooterContent={SidebarFooter}
        MainContent={SidebarMain}
        footerLinks={footerLink}
        mainLinks={linksEnabled}
        maxPathNameRange={2}
      />
    </>
  );
}
